// app/components/approval/ApprovalCard.tsx
import ApprovalAmount from "./ApprovalAmount";
import ApprovalBadge from "./ApprovalBadge";
import ApprovalActionButtons from "./ApprovalActionButtons";
import type {
  ApprovalCardItem,
  ApprovalRawRecord,
  ApprovalTabId,
} from "../../types/approval";
import { t } from "i18next";
import {
  getInitials,
  isValidDisplayValue,
} from "@/app/utils/helpers/textHelpers";
import type { DashboardPendingApprovalViewSummaryResponse } from "@/app/types/alterNativeDashboardLayout";

interface ApprovalCardProps {
  item: ApprovalCardItem;
  onReject?: (transId: number) => void;
  onApprove?: (transId: number) => void;
  onClick?: (item: ApprovalCardItem) => void;
}

const NO_AVATAR_TABS = new Set(["bulk", "payrolls"]);

const getBeneInitials = (name?: string) =>
  isValidDisplayValue(name) ? name!.slice(0, 3).toUpperCase() : "";

const ApprovalCard = ({
  item,
  onReject,
  onApprove,
  onClick,
}: ApprovalCardProps) => {
  const isActionDisabled =
    item.numberOfApproved >= item.numberOfLevels ||
    item.paymentStatusCode === 4447;

  const isBeneficiaryTab = item.tabId === "bene";
  // const isAllTab = item.tabId === "all";
  const showAvatar = !NO_AVATAR_TABS.has(item.tabId);

  let initials = "";
  let titleContent: React.ReactNode;
  let subtitleContent: React.ReactNode;
  let bottomContent: React.ReactNode = null;

  if (isBeneficiaryTab) {
    initials = getBeneInitials(item.beneAccountHolderName);

    titleContent = isValidDisplayValue(item.beneAccountHolderName)
      ? item.beneAccountHolderName
      : "--";

    subtitleContent = (
      <>
        {t("requested")} {item.requestedDate} {item.referenceNumber}
      </>
    );

    if (isBeneficiaryTab) {
      subtitleContent = (
        <>
          {t("requested")} {item.requestedDate} {item.referenceNumber}
        </>
      );
    } else {
      subtitleContent = (
        <>
          {t("requested")} {item.requestedDate} &middot; {t("ref")}{" "}
          {item.referenceNumber}
        </>
      );
    }
  } else if (item.tabId === "bulk" || item.tabId === "payrolls") {
    titleContent = isValidDisplayValue(item.referenceNumber)
      ? item.referenceNumber
      : "--";
    subtitleContent = (
      <>
        {t("requested")}{" "}
        <span className="gx-approval-card-alt__dot">&middot;</span>{" "}
        {item.requestedDate}
      </>
    );
    bottomContent = null;
  } else if (item.tabId === "txn") {
    initials = getInitials(item.beneAccountName);

    titleContent = isValidDisplayValue(item.beneAccountName)
      ? item.beneAccountName
      : "--";

    subtitleContent = (
      <>
        {t("requested")} {item.requestedDate} &middot; {t("ref")}{" "}
        {item.referenceNumber}
      </>
    );
  } else if (
    (item.tabId as ApprovalTabId) === "all" &&
    (item.raw as ApprovalRawRecord)?.category === "txn"
  ) {
    initials = getInitials(item.accountName);

    titleContent = isValidDisplayValue(item.accountName)
      ? item.accountName
      : "--";

    subtitleContent = (
      <>
        {t("requested")} {item.requestedDate} &middot; {t("ref")}{" "}
        {item.referenceNumber}
      </>
    );
  } else {
    const displayName = isValidDisplayValue(item.accountName)
      ? item.accountName
      : item.beneAccountName;
    initials = getInitials(displayName);

    titleContent = isValidDisplayValue(displayName) ? displayName : "--";

    subtitleContent = (
      <>
        {t("requested")} {item.requestedDate} &middot; {t("ref")}
        {item.referenceNumber}
      </>
    );
  }

  return (
    <button
      type="button"
      className="gx-approval-card-alt"
      onClick={() => onClick?.(item)}
    >
      {showAvatar && (
        <div className="gx-approval-card-alt__avatar" aria-hidden="true">
          {initials}
        </div>
      )}

      <div className="gx-approval-card-alt__main">
        <div className="gx-approval-card-alt__title gx-p14-b">
          {titleContent}
        </div>

        <div className="gx-approval-card-alt__subtitle gx-p12-m">
          {bottomContent && <>{bottomContent} &middot; </>}
          {subtitleContent}
        </div>
      </div>

      <div className="gx-approval-card-alt__amount">
        {!(
          isBeneficiaryTab ||
          (item.tabId === "all" &&
            (item.raw as DashboardPendingApprovalViewSummaryResponse)
              ?.moduleType === "BENEFICIARY")
        ) && (
          <ApprovalAmount
            amount={item.amount}
            currencyCode={item.currencySymbol}
          />
        )}

        <div className="gx-no_approval-card">
          <ApprovalBadge
            numberOfApproved={item.numberOfApproved}
            numberOfLevels={item.numberOfLevels}
          />
        </div>
      </div>

      <ApprovalActionButtons
        transId={item.transId}
        onReject={onReject}
        onApprove={onApprove}
        disabled={isActionDisabled}
      />
    </button>
  );
};

export default ApprovalCard;
